import React from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store/store';
import { Layout } from './components/Layout';
import Home from './components/Home';
import HomeDebug from './components/Homepage/HomeDebug';
import EventDetailsEnhanced from './components/EventDetailsEnhanced';
import ItemDetailsPage from './components/ItemDetailsPage';
import UserDashboard from './components/UserDashboard';
import CreateEvent from './components/CreateEvent';
import CreateItem from './components/CreateItem';
import EditEvent from './components/EditEvent';
import EditItem from './components/EditItem';
import AuthInitializer from './components/AuthInitializer';
import DebugEventDetails from './components/DebugEventDetails';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConfirmLocalAlertPage from './pages/ConfirmLocalAlertPage';
import UnsubscribeEmailPage from './pages/UnsubscribeEmailPage';
import TraderProfilePage from './pages/TraderProfilePage';
import MyMathTradePreferencesPage from './pages/MyMathTradePreferencesPage';
import FaqPage from './pages/FaqPage';
import EventCheckInPage from './pages/EventCheckInPage';
import EventDayGuidePage from './pages/EventDayGuidePage';
import SiteAdminRuntimeSettingsPage from './pages/SiteAdminRuntimeSettingsPage';
import SiteAdminHomePage from './pages/SiteAdminHomePage';
import SiteAdminOperationsPage from './pages/SiteAdminOperationsPage';
import SiteAdminTradeSessionMetricsPage from './pages/SiteAdminTradeSessionMetricsPage';
import SiteAdminUsersPage from './pages/SiteAdminUsersPage';
import BggImportPreviewPage from './pages/BggImportPreviewPage';
import MailboxPage from './pages/MailboxPage';

// Create CrowdTrades theme
const theme = createTheme({
  palette: {
    primary: { main: '#667eea' },
    secondary: { main: '#764ba2' },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff'
    }
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Arial',
      'sans-serif'
    ].join(','),
    h1: { fontWeight: 700 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
          }
        }
      }
    }
  }
});

function App() {
  return (
    <Provider store={store}>
      <AuthInitializer />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Layout>
            <Routes>
              {/* Main Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/confirm-local-alert" element={<ConfirmLocalAlertPage />} />
              <Route path="/unsubscribe-email" element={<UnsubscribeEmailPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/event-day-guide" element={<EventDayGuidePage />} />
              <Route path="/site-admin" element={<SiteAdminHomePage />} />
              <Route path="/site-admin/runtime-settings" element={<SiteAdminRuntimeSettingsPage />} />
              <Route path="/site-admin/operations" element={<SiteAdminOperationsPage />} />
              <Route path="/site-admin/trade-session-metrics" element={<SiteAdminTradeSessionMetricsPage />} />
              <Route path="/site-admin/users" element={<SiteAdminUsersPage />} />
              <Route path="/events/:eventId" element={<EventDetailsEnhanced />} />
              <Route path="/events/:eventId/check-in" element={<EventCheckInPage />} />
              <Route path="/events/:eventId/items/:itemId" element={<ItemDetailsPage />} />
              <Route path="/users/:userId" element={<TraderProfilePage />} />
              <Route path="/mailbox" element={<MailboxPage />} />
              <Route path="/events/:eventId/my-wantlists" element={<MyMathTradePreferencesPage />} />
              
              {/* User Dashboard Routes */}
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/create-event" element={<CreateEvent />} />
              <Route path="/create-item" element={<CreateItem />} />
              <Route path="/inventory/import/bgg" element={<BggImportPreviewPage />} />
              <Route path="/edit-event/:eventId" element={<EditEvent />} />
              <Route path="/edit-item/:itemId" element={<EditItem />} />
              
              {/* Debug Routes */}
              <Route path="/debug" element={<HomeDebug />} />
              <Route path="/debug-event/:eventId" element={<DebugEventDetails />} />
            </Routes>
          </Layout>
        </Router>

        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
